import React from 'react'
import { useTranslation } from 'react-i18next'

import { compile } from '~/apps/ninesegsAnalyzer/routes/paths'
import { Suspense } from '~/components/elements/Suspense'
import {
  RedirectOrError,
  type RedirectOrErrorProps,
} from '~/components/features/RedirectOrError'
import { useCommonQueryCurrentBrandAccount } from '~/hooks/useCommonQuery'
import {
  type Survey,
  useCurrentBrandAccountSurveysQuery,
} from '~/hooks/useCurrentBrandAccountSurveysQuery'
import { useSurveyBrandAccountQuery } from '~/hooks/useSurveyBrandAccountQuery'
import { useSurveyQuery } from '~/hooks/useSurveyQuery'
import { exists, SurveyOp } from '~/utils'

export const SurveyPage = () => {
  const { t } = useTranslation()
  const { data: surveyData, isLoading: isLoadingSurvey } = useSurveyQuery()
  const survey = surveyData?.survey
  const {
    data: currentBrandAccountSurveys,
    isLoading: isLoadingCurrentBrandAccountSurveys,
  } = useCurrentBrandAccountSurveysQuery()
  const { isLoading: isLoadingSurveysBrandAccount } =
    useSurveyBrandAccountQuery(survey?.id)
  const currentBrandAccount = useCommonQueryCurrentBrandAccount()

  const getRedirectPath = React.useCallback<
    RedirectOrErrorProps['getRedirectPath']
  >(() => {
    if (!exists(survey) || !exists<Survey[]>(currentBrandAccountSurveys)) {
      return { success: false, error: t('survey_not_found_1') }
    }

    const readiedSurveys = currentBrandAccountSurveys.filter((survey) =>
      SurveyOp.hasMappedBrandGroupsWithBrands(survey),
    )
    if (
      readiedSurveys.some((readiedSurvey) => readiedSurvey.id === survey.id)
    ) {
      return {
        success: true,
        path: compile('customerAnalysis', { surveyId: survey.id }),
      }
    }

    if (currentBrandAccount?.isSurveyEditor) {
      return {
        success: true,
        path: compile('surveySettings', { surveyId: survey.id }),
      }
    }

    return {
      success: false,
      error: t('cannot_be_used_because_setup_is_not_complete'),
    }
  }, [currentBrandAccount, currentBrandAccountSurveys, survey, t])

  const isLoading =
    isLoadingSurvey ||
    isLoadingCurrentBrandAccountSurveys ||
    isLoadingSurveysBrandAccount

  return (
    <Suspense loading={isLoading} loadingContainerSize="full">
      <RedirectOrError getRedirectPath={getRedirectPath} />
    </Suspense>
  )
}
